import React, { Fragment } from 'react';
import PropTypes from 'prop-types'
import * as types from '../../../constants/actionTypes';
import { FILE_STATUSES } from '../../../constants/constants';
import Notification from '../../Common/Notification';
import LoadingIndicator from '../../Common/LoadingIndicator/LoadingIndicator';
import Icon from '../../Common/Icon';
import Button from '../../Common/Button';
import FileTypeIcon from '../../Common/FileTypeIcon';
import LocationFileManagerDrawer from '../../Common/LocationFileManagerDrawer/LocationFileManagerDrawer';
import HistoricalProjectsToggle from './HistoricalProjectsToggle';
import './LocationFiles.scss';

class LocationFiles extends React.Component {
    constructor(props, context) {
        super(props, context);

        this.state = {
            disciplines: [],
            isSavingFile: false,
            isLoading: false,
            isFileManagerDrawerOpen: false,
            selectedFileId: null,
            showHistoricalFiles: false
        }
    }

    componentDidMount = async () => {
        await this.loadFiles();
    }

    componentDidUpdate(prevProps) {
        // Reload files when user changes dashboard location
        if (this.props.locationKey && this.props.locationKey !== prevProps.locationKey) {
            this.loadFiles(this.state.showHistoricalFiles);
        }
    }

    loadFiles = async (includeHistorical = false) => {
        this.setState({ isLoading: true });
        try {
            const response = includeHistorical 
                ? await this.props.locationDashboardActions.loadLocationFilesWithHistorical(this.props.locationKey)
                : await this.props.locationDashboardActions.loadLocationFiles(this.props.locationKey);
            
            if (response.type === types.LOAD_LOCATION_DASHBOARD_FILES_SUCCESS) {
                const discs = response.payload.disciplines;
                this.setState({
                    disciplines: discs
                });
                return true;
            } else if (response.type === types.LOAD_LOCATION_DASHBOARD_FILES_FAILURE) {
                Notification("error", "Error", response.payload.message);
                return false;
            }
        } catch (error) {
            Notification("error", "Error", error);
            return false;
        } finally {
            this.setState({ isLoading: false });
        }
    }

    onDownloadFile = async (file, e) => {
        e.preventDefault();
        try {
            this.setState({ isSavingFile: true });
            await this.props.filesActions.download(file.fileName);
            this.setState({ isSavingFile: false });
        } catch (error) {
            Notification("error", "Error", error);
            this.setState({ isSavingFile: false });
        }
    }

    onShowFileManagerDrawer = (file) => {
        const fileId = file ? file.id.toString() : null;
        this.setState({ isFileManagerDrawerOpen: true, selectedFileId: fileId });
    }
    onFileManagerCancel = () => {
        this.setState({ isFileManagerDrawerOpen: false });
    }

    onFileManagerComplete = () => {
        this.setState({ isFileManagerDrawerOpen: false }, () => this.loadFiles(this.state.showHistoricalFiles));
    }

    onToggleHistoricalFiles = async () => {
        const newShowHistoricalFiles = !this.state.showHistoricalFiles;
        const success = await this.loadFiles(newShowHistoricalFiles);
        if (success) {
            this.setState({ showHistoricalFiles: newShowHistoricalFiles });
        }
    }

    render() {
        return (
            <div className="location-files">
                <div className="d-flex align-items-center justify-content-between">
                    <div className="dashboard-widget-title">Files</div>
                    <span>
                        <Button
                            type="button"
                            text="Add New File"
                            bootstrapClass="btn btn-primary new-file"
                            onClick={() => this.onShowFileManagerDrawer(null)}
                        />
                    </span>
                </div>

                {this.state.disciplines && this.state.disciplines.length > 0 && this.state.disciplines.map((disc, index) => {
                    return (
                        <Fragment key={index}>
                            <div className="disc-name mb-2">{disc.name}</div>
                            {disc.departments && disc.departments.length > 0 && disc.departments.map(dept => {
                                const files = dept.files;

                                return (
                                    <div key={dept.name} className="card mt-2">
                                        <div className="card-header text-capitalize">{dept.name}</div>
                                        <div className="card-body">
                                            <LoadingIndicator isLoading={this.state.isLoading}>
                                                {files && files.length > 0 && files.map(file => {
                                                    const fileIsApprovedOrDenied = file.fileStatusId === FILE_STATUSES.Approved || file.fileStatusId === FILE_STATUSES.Denied;
                                                    const fileNameClass = fileIsApprovedOrDenied ? "col-9" : "col-10";

                                                    return (
                                                        <div key={file.fileName} className="file row mb-2 g-0">
                                                            <span className="col-1 text-start"><FileTypeIcon extension={file.extension} /></span>
                                                            <button
                                                                className={`btn btn-link ${fileNameClass} text-start`}
                                                                title="Download"
                                                                onClick={(e) => this.onDownloadFile(file, e)}
                                                            >
                                                                {file.name}
                                                            </button>
                                                            {file.fileStatusId === FILE_STATUSES.Approved ? (
                                                                <span className="col-1 text-success" title="File Approved">
                                                                    <Icon name="check-circle" />
                                                                </span>
                                                            ) : file.fileStatusId === FILE_STATUSES.Denied ? (
                                                                <span className="col-1 text-danger" title="File Denied">
                                                                    <Icon name="times-circle" />
                                                                </span>
                                                            ) : null}
                                                            <span
                                                                className="col-1 text-primary cursor-pointer"
                                                                title="Edit"
                                                                onClick={() => this.onShowFileManagerDrawer(file)}
                                                            >
                                                                <Icon name="pencil-alt" />
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </LoadingIndicator>
                                        </div>
                                    </div>
                                );
                            })}
                        </Fragment>

                    );
                })}

                <HistoricalProjectsToggle
                    showHistoricalFiles={this.state.showHistoricalFiles}
                    onToggle={this.onToggleHistoricalFiles}
                    isLoading={this.state.isLoading}
                />

                {this.state.isFileManagerDrawerOpen &&
                    <LocationFileManagerDrawer
                        onComplete={this.onFileManagerComplete}
                        onCancel={this.onFileManagerCancel}
                        locationKey={this.props.locationKey}
                        fileId={this.state.selectedFileId}
                    />
                }
            </div>
        );
    }

}

LocationFiles.propTypes = {
    locationKey: PropTypes.string.isRequired,
    locationDashboardActions: PropTypes.object.isRequired,
    locationTaskActions: PropTypes.object.isRequired,
    filesActions: PropTypes.object.isRequired,
};

export default LocationFiles;
